import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { checkListItem, OPERATIONS_LIST_ERROR_MESSAGES } from '@wodi/domain';
import type { OperationsListItemView, Permission, SaveOperationsListRequest } from '@wodi/shared';
import { Button, Checkbox, LoadingBlock, Notice, Section, TextField } from '@wodi/ui';
import { useState } from 'react';

import { fetchIncidentOptions, incidentKeys, saveIncidentList } from '../api/incidents.ts';
import { fetchInfectionOptions, infectionKeys, saveInfectionList } from '../api/infection.ts';
import { assetKeys, fetchAssetOptions, saveAssetCategories } from '../api/assets.ts';
import { fetchDietOptions, kitchenKeys, saveDietList } from '../api/kitchen.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import { errorText, LoadFailed } from './format.tsx';
import styles from './operations.module.css';

// Settings > Operations lists (docs/plans/phase3-operations-modules.md §0.1, ADR 0036): the
// incident categories and harm scale, and the lists later modules add. Each list is shown to
// the holders of its module's manage permission. Codes are fixed once saved; items are
// switched off, never removed, so records keep their words. TODO(clinical-verify): the
// starting lists are provisional.

export interface ListConfig {
  key: string;
  title: string;
  description: string;
  permission: Permission;
  queryKey: QueryKey;
  load: () => Promise<OperationsListItemView[]>;
  save: (body: SaveOperationsListRequest) => Promise<OperationsListItemView[]>;
}

export const OPERATIONS_LISTS: ListConfig[] = [
  {
    key: 'incident_category',
    title: 'Incident categories',
    description: 'What kind of incident it was. Shown on the report form in this order.',
    permission: 'incidents.manage',
    queryKey: ['operations-lists', 'incident_category'],
    load: async () => (await fetchIncidentOptions()).categories,
    save: (body) => saveIncidentList('incident_category', body),
  },
  {
    key: 'incident_severity',
    title: 'Harm scale',
    description: 'How much harm was done, the least first: the order is the rank.',
    permission: 'incidents.manage',
    queryKey: ['operations-lists', 'incident_severity'],
    load: async () => (await fetchIncidentOptions()).severities,
    save: (body) => saveIncidentList('incident_severity', body),
  },
  {
    key: 'isolation_precaution',
    title: 'Precautions',
    description:
      'Isolation and precaution flags, as the banner shows them ("Contact" reads "Contact precautions").',
    permission: 'infection.manage',
    queryKey: ['operations-lists', 'isolation_precaution'],
    load: async () => (await fetchInfectionOptions()).precautions,
    save: (body) => saveInfectionList('isolation_precaution', body),
  },
  {
    key: 'infection_category',
    title: 'Infection categories',
    description: 'Hospital-acquired infection categories for the line list.',
    permission: 'infection.manage',
    queryKey: ['operations-lists', 'infection_category'],
    load: async () => (await fetchInfectionOptions()).categories,
    save: (body) => saveInfectionList('infection_category', body),
  },
  {
    key: 'asset_category',
    title: 'Asset categories',
    description: 'What kind of equipment an asset is, for the register.',
    permission: 'assets.manage',
    queryKey: ['operations-lists', 'asset_category'],
    load: async () => (await fetchAssetOptions()).categories,
    save: saveAssetCategories,
  },
  {
    // Kitchen plan K1, TODO(clinical-verify): diet names only; no rule is applied to them.
    key: 'diet_type',
    title: 'Diet types',
    description: 'The diets clinicians order and the kitchen prepares.',
    permission: 'kitchen.manage',
    queryKey: ['operations-lists', 'diet_type'],
    load: async () => (await fetchDietOptions()).diets,
    save: (body) => saveDietList('diet_type', body),
  },
  {
    key: 'diet_texture',
    title: 'Diet textures',
    description: 'How the food is prepared, for patients who need a softer texture.',
    permission: 'kitchen.manage',
    queryKey: ['operations-lists', 'diet_texture'],
    load: async () => (await fetchDietOptions()).textures,
    save: (body) => saveDietList('diet_texture', body),
  },
];

function ListEditor({
  config,
  items: saved,
  online,
}: {
  config: ListConfig;
  items: OperationsListItemView[];
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState(saved);
  const [code, setCode] = useState('');
  const [display, setDisplay] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => config.save({ items }),
    onSuccess: (next) => {
      setItems(next);
      queryClient.setQueryData(config.queryKey, next);
      void queryClient.invalidateQueries({ queryKey: incidentKeys.options });
      void queryClient.invalidateQueries({ queryKey: infectionKeys.options });
      void queryClient.invalidateQueries({ queryKey: assetKeys.options });
      void queryClient.invalidateQueries({ queryKey: kitchenKeys.options });
      toasts.show({ title: `${config.title} saved` });
    },
  });
  const change = (index: number, patch: Partial<OperationsListItemView>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };
  const move = (index: number, by: number) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    if (item === undefined) return;
    next.splice(index + by, 0, item);
    setItems(next);
  };
  return (
    <Section title={config.title} description={config.description}>
      <div className={styles.form}>
        <ul className={styles.list} aria-label={config.title}>
          {items.map((item, index) => (
            <li key={item.code} className={styles.listEditorRow}>
              <span className={`wodi-id ${styles.meta}`}>{item.code}</span>
              <TextField
                label={`Name for ${item.code}`}
                value={item.display}
                onChange={(value) => {
                  change(index, { display: value });
                }}
              />
              <Checkbox
                isSelected={item.active}
                onChange={(active) => {
                  change(index, { active });
                }}
              >
                Switched on
              </Checkbox>
              <span className={styles.actions}>
                <Button
                  dense
                  variant="quiet"
                  isDisabled={index === 0}
                  onPress={() => {
                    move(index, -1);
                  }}
                  aria-label={`Move ${item.display} up`}
                >
                  Up
                </Button>
                <Button
                  dense
                  variant="quiet"
                  isDisabled={index === items.length - 1}
                  onPress={() => {
                    move(index, 1);
                  }}
                  aria-label={`Move ${item.display} down`}
                >
                  Down
                </Button>
              </span>
            </li>
          ))}
        </ul>
        <div className={styles.row}>
          <TextField
            label="New code"
            description="Lower-case letters, digits and _, fixed once saved."
            value={code}
            onChange={setCode}
            width="md"
          />
          <TextField label="New name" value={display} onChange={setDisplay} width="lg" />
          <Button
            onPress={() => {
              const checked = checkListItem({ code: code.trim(), display });
              if (!checked.ok) {
                setAddError(OPERATIONS_LIST_ERROR_MESSAGES[checked.code]);
                return;
              }
              if (items.some((i) => i.code === checked.value.code)) {
                setAddError(OPERATIONS_LIST_ERROR_MESSAGES['operations_list.duplicate_code']);
                return;
              }
              setAddError(null);
              setItems([...items, { ...checked.value, active: true }]);
              setCode('');
              setDisplay('');
            }}
          >
            Add to list
          </Button>
        </div>
        {addError === null ? null : <Notice tone="error">{addError}</Notice>}
        {save.isError ? (
          <Notice tone="error">{errorText(save.error, "The list wasn't saved. Try again.")}</Notice>
        ) : null}
        <div className={styles.actions}>
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={save.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              save.mutate();
            }}
          >
            {`Save ${config.title.toLowerCase()}`}
          </Button>
          {online ? null : (
            <p className={styles.meta}>Saving works again when you&apos;re back online.</p>
          )}
        </div>
      </div>
    </Section>
  );
}

function ListSection({ config, online }: { config: ListConfig; online: boolean }) {
  const list = useQuery({ queryKey: config.queryKey, queryFn: config.load });
  if (list.isPending) return <LoadingBlock label={`Loading ${config.title}`} rows={4} />;
  if (list.isError)
    return (
      <LoadFailed
        error={list.error}
        fallback={`${config.title} couldn't be loaded. Try again.`}
        onRetry={() => void list.refetch()}
      />
    );
  return <ListEditor config={config} items={list.data} online={online} />;
}

export function OperationsListsSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const lists = session === null ? [] : OPERATIONS_LISTS.filter((l) => can(session, l.permission));
  return (
    <SettingsPage title="Operations lists">
      {lists.length === 0 ? (
        <Notice tone="info">
          You don&apos;t have permission to change these lists. Ask an administrator if you need it.
        </Notice>
      ) : (
        <div className={styles.page}>
          {lists.map((config) => (
            <ListSection key={config.key} config={config} online={online} />
          ))}
        </div>
      )}
    </SettingsPage>
  );
}
